// src/pages/SongSubmission.jsx
import React, { useState } from "react";
import axios from "../services/api";

// Match backend enum values
const INTEREST_OPTIONS = [
  { label: "Gaining exposure", value: "GAINING_EXPOSURE" },
  { label: "Performing at our events", value: "PERFORMING_AT_EVENTS" },
  { label: "Help and guidance", value: "HELP_AND_GUIDANCE" },
  { label: "All of the above", value: "ALL_OF_THE_ABOVE" },
];

const MAX_MB = 20;

export default function SongSubmission() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    link: "",
    birthDate: "",
    interests: [],
    mp3: null,
  });

  const [submitting, setSubmitting] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleInterest = (value) => {
    setForm((f) => {
      const has = f.interests.includes(value);
      return { ...f, interests: has ? f.interests.filter((v) => v !== value) : [...f.interests, value] };
    });
  };

  const onFile = (file) => {
    setOkMsg("");
    setErrMsg("");

    if (file) {
      const sizeMb = file.size / (1024 * 1024);
      const typeOk =
        file.type === "audio/mpeg" ||
        file.type === "audio/mp3" ||
        file.name.toLowerCase().endsWith(".mp3");

      if (!typeOk) {
        setErrMsg("File must be an MP3.");
        update("mp3", null);
        return;
      }
      if (sizeMb > MAX_MB) {
        setErrMsg(`MP3 must be ${MAX_MB}MB or less.`);
        update("mp3", null);
        return;
      }
    }
    update("mp3", file || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setOkMsg("");
    setErrMsg("");

    if (!form.firstName.trim()) return setErrMsg("First name is required.");
    if (!form.lastName.trim()) return setErrMsg("Last name is required.");
    if (!form.email.trim()) return setErrMsg("Email is required.");
    if (!form.birthDate) return setErrMsg("Birth date is required.");
    if (!form.mp3) return setErrMsg("Please attach an MP3 file.");

    const fd = new FormData();
    fd.append("firstName", form.firstName);
    fd.append("lastName", form.lastName);
    fd.append("email", form.email);
    if (form.phone) fd.append("phone", form.phone);
    if (form.link) fd.append("link", form.link);
    fd.append("birthDate", form.birthDate);
    form.interests.forEach((i) => fd.append("interests", i));
    fd.append("mp3", form.mp3);

    setSubmitting(true);
    try {
      await axios.post("/api/submissions/song", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOkMsg("Thanks! Your song has been submitted.");
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        link: "",
        birthDate: "",
        interests: [],
        mp3: null,
      });
      const fileEl = document.getElementById("song-mp3-input");
      if (fileEl) fileEl.value = "";
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Submission failed. Please try again.";
      setErrMsg(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page space-y-6">
      {/* Header */}
      <section className="card">
        <div className="md:flex md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl">Submit your track</h1>
            <p className="mt-1 text-slate-300">
              MP3 only, up to {MAX_MB}MB. Your submission goes straight to our A&R team.
            </p>
          </div>
          <span className="badge bg-brand-600/20">House • Tech • Minimal • Deep</span>
        </div>
      </section>

      {/* Alerts */}
      {okMsg && (
        <div className="card bg-green-500/10 border border-green-400/30 text-green-200">
          {okMsg}
        </div>
      )}
      {errMsg && (
        <div className="card bg-red-500/10 border border-red-500/30 text-red-200">
          {errMsg}
        </div>
      )}

      {/* Form */}
      <section className="card">
        <form onSubmit={submit} className="space-y-6">
          {/* Names */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="field">
              <label className="label">First name *</label>
              <input
                className="input"
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                required
                placeholder="Jane"
              />
            </div>
            <div className="field">
              <label className="label">Last name *</label>
              <input
                className="input"
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="field">
              <label className="label">Email *</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                required
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div className="field">
              <label className="label">Phone</label>
              <input
                className="input"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="Optional"
                autoComplete="tel"
              />
            </div>
          </div>

          {/* Birth date + external link */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="field">
              <label className="label">Birth date *</label>
              <input
                type="date"
                className="input"
                value={form.birthDate}
                onChange={(e) => update("birthDate", e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label className="label">Spotify / SoundCloud link</label>
              <input
                type="url"
                className="input"
                value={form.link}
                onChange={(e) => update("link", e.target.value)}
                placeholder="https://open.spotify.com/track/... or https://soundcloud.com/..."
              />
            </div>
          </div>

          {/* Interests (as chips) */}
          <div className="field">
            <label className="label">I’m interested in</label>
            <div className="grid gap-2 sm:grid-cols-2">
              {INTEREST_OPTIONS.map((opt) => {
                const checked = form.interests.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition
                    ${checked ? "border-brand-600 bg-brand-600/10 text-white" : "border-white/10 bg-white/5 text-slate-200"}
                    hover:bg-white/10`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() => toggleInterest(opt.value)}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="help">Pick as many as you like.</p>
          </div>

          {/* File upload */}
          <div className="field">
            <label className="label">Song (MP3, ≤ {MAX_MB}MB) *</label>

            {/* Drop zone + input */}
            <div
              className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-4 text-sm text-slate-300
                         hover:bg-white/10 transition"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) onFile(file);
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  {form.mp3 ? (
                    <>
                      <div className="font-medium text-white">{form.mp3.name}</div>
                      <div className="text-xs text-slate-400">
                        {(form.mp3.size / (1024 * 1024)).toFixed(1)} MB
                      </div>
                    </>
                  ) : (
                    <div className="text-slate-300">
                      Drag & drop your MP3 here, or choose a file.
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  <label className="btn-secondary cursor-pointer">
                    <input
                      id="song-mp3-input"
                      type="file"
                      accept=".mp3,audio/mpeg,audio/mp3"
                      className="hidden"
                      onChange={(e) => onFile(e.target.files?.[0] || null)}
                      required={!form.mp3}
                    />
                    Browse files
                  </label>
                </div>
              </div>
            </div>

            <p className="help">Only MP3 files are accepted. WAV/AIFF coming soon.</p>
          </div>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit track"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
